import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, ThemeProvider, Typography, Link,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { useMutation } from '@apollo/client';

import {
  ContentContainer, ErrorText, CodeInputContainer, CodeInput,
} from './AccessCodeEntry.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product, getProductSupportEmail, getPdtUid } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { VERIFY_ACCESS_CODE, GET_AUTH_METHODS_FOR_ACCESS_CODE } from '@/apollo/mutations';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { useAdoptPostHogIdentity } from '@/hooks/useAdoptPostHogIdentity';

const CODE_LENGTH = 6;
const INPUT_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

interface AccessCodeEntryLocationState {
  accessCode?: string;
  /**
   * Set by AccessCodeSpark's Yes button once it has settled identity for this
   * activation, so this screen does not reset a second time mid-flow. Absent on
   * the prefilled-code redirect and on a direct deep link, where this screen is
   * the real entry point and has to make the decision itself.
   */
  fromFlowEntry?: boolean;
}

const AccessCodeEntryInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const history = useHistory();
  const { state: locationState } = useLocation<AccessCodeEntryLocationState>();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const [code, setCode] = useState(() => {
    const prefill = (locationState?.accessCode ?? '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    const arr = prefill.split('');
    while (arr.length < CODE_LENGTH) { arr.push(''); }
    return arr;
  });
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const program = getProgram(product);
  const supportEmail = getProductSupportEmail(product);
  const pdtUid = getPdtUid(product);
  const isComplete = code.every((digit) => digit !== '');
  const [verifyAccessCodeMutation] = useMutation(VERIFY_ACCESS_CODE);
  const [getAuthMethods] = useMutation(GET_AUTH_METHODS_FOR_ACCESS_CODE);

  const { loading: identityLoading } = useAdoptPostHogIdentity({
    skipReset: Boolean(locationState?.fromFlowEntry),
    onReady: () => {
      posthog?.capture('AccessCodeLanded');
    },
  });

  // Auto-focus first empty input, or skip if all slots are pre-filled
  useEffect(() => {
    const firstEmpty = code.findIndex((d) => !d);
    if (firstEmpty !== -1) { inputRefs.current[firstEmpty]?.focus(); }
  }, []);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();
  const updateCode = (index: number, digit: string) => {
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    if (error) { setError(''); }
  };

  // Handle typing in an input
  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    updateCode(index, digit);
    if (digit && index < CODE_LENGTH - 1) { focusInput(index + 1); }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    const newCode = [...code];
    digits.split('').forEach((digit, i) => {
      if (index + i < CODE_LENGTH) {
        newCode[index + i] = digit;
      }
    });
    setCode(newCode);
    if (error) { setError(''); }
    const nextEmpty = newCode.findIndex((d) => !d);
    focusInput(nextEmpty === -1 ? CODE_LENGTH - 1 : nextEmpty);
  };

  // Validate access code with Cortex
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Held while a partner ph_token is still decoding. A prefilled code arrives
    // ready to submit, so without this the whole validation sequence —
    // AccessCodeSubmitted through AccessCodeSucceeded — could fire under the
    // previous person's identity, and navigating on would unmount this screen
    // before its decode resolved, leaving the partner identity never adopted at
    // all. CreateAccount then sends that stale id to the backend as the
    // patient's. Guarded here as well as on the button so a keyboard submit
    // cannot slip past.
    if (!isComplete || identityLoading) { return; }

    const codeString = code.join('');
    setIsValidating(true);
    setError('');
    posthog?.register({ access_code: codeString });
    posthog?.capture('AccessCodeSubmitted');
    try {
      const result = await verifyAccessCodeMutation({
        variables: {
          accessCode: codeString,
          pdtUid,
          isStudyCode: false,
        },
      });
      const isValid = result?.data?.verifyAccessCode?.valid;
      if (isValid) {
        posthog?.capture('SparkAccessCodeValidationSuccess', { program });
        posthog?.capture('AccessCodeSucceeded');

        // Fetch allowed auth methods for this access code (empty = show both)
        const authMethods: string[] = await getAuthMethods({
          variables: { accessCode: codeString, pdtUid },
        }).then(
          ({ data }) => (data?.getAuthMethodsForAccessCode?.authMethods ?? [])
            .filter((method: string | null | undefined): method is string => !!method),
          (): string[] => [],
        );

        history.push(`/activate/${product}/access-code/entry/create-account`, {
          accessCode: codeString,
          authMethods,
        });
      } else {
        posthog?.capture('SparkAccessCodeValidationFailed');
        posthog?.capture('AccessCodeFailed');
        setError(t('coverage.pre_onboarding.access_code_entry.error'));
      }
    } catch (err) {
      posthog?.capture('SparkAccessCodeValidationError');
      posthog?.capture('AccessCodeErrored');
      posthog?.capture('AccessCodeFailed');
      setError(t('coverage.pre_onboarding.access_code_entry.error'));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.pre_onboarding.access_code_entry.title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.access_code_entry.sub_title')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box style={{ marginBottom: 24 }}>
            <CodeInputContainer data-testid="access-code-input">
              {INPUT_POSITIONS.map((i) => (
                <CodeInput
                  key={i}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[i]}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={(e) => handlePaste(i, e)}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  data-testid={`code-input-${i}`}
                  className={error ? 'error' : ''}
                  aria-label={t('coverage.pre_onboarding.access_code_entry.input_aria_label', { position: i + 1 })}
                />
              ))}
            </CodeInputContainer>
            {error && <ErrorText data-testid="error-message">{error}</ErrorText>}
          </Box>

          <Box style={{ marginTop: 32, marginBottom: 40, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {t('coverage.pre_onboarding.access_code_entry.help_prefix')}
              {' '}
              <Link
                href={`mailto:${supportEmail}`}
                underline="always"
                color="inherit"
                onClick={() => posthog?.capture('SupportContacted')}
              >
                {supportEmail}
              </Link>
              {' '}
              {t('coverage.pre_onboarding.access_code_entry.help_suffix')}
            </Typography>
          </Box>

          <Button
            variant="contained"
            data-testid="continue-button"
            type="submit"
            fullWidth
            color="primary"
            disabled={!isComplete || isValidating || identityLoading}
            rounded
          >
            {isValidating
              ? t('coverage.pre_onboarding.access_code_entry.validating')
              : t('coverage.pre_onboarding.access_code_entry.continue')}
          </Button>
        </form>

        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const AccessCodeEntry: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <AccessCodeEntryInner />
  </ThemeProvider>
);

export default AccessCodeEntry;
